








































import {
  defineComponent,
  onMounted,
  PropType,
  ref,
  computed,
  onBeforeUnmount,
} from '@vue/composition-api';
import { Button, Toast } from '@hb/coffee';
import {
  ContractItemDetail,
  CreditMallFromEnum,
  MiddleIncomeContractListResponse,
  MiddleIncomeCreditApplyResponse,
  MiddleIncomeDispatchResponse,
  PageStatusEnum,
  ProductContractList,
} from '@/api/middle-income/types';
import { applyMiddleIncomeCredit } from '@/api/middle-income';

import { openThirdPage } from '@/api/native/utils';
import { ubtModuleExposeNew, ubtPageButtonNew } from '@/shared/utils/ubt';
import router from '@/router';
import ContractViewWithTab from './ContractViewWithTab.vue';
import { pollingStatus } from '../middle-income-card/pollingStatus';
import {
  shopMallAgreementPopupExpose,
  shopMallAgreementPopupClick,
  dcHalfApplyAgreementExpose,
  dcHalfApplyAgreementClick,
} from '../middle-income-card/tianqi';

export default defineComponent({
  components: {
    ContractViewWithTab,
    Button,
  },
  props: {
    /** 完整分发结果 */
    dispatchResult: {
      type: Object as PropType<MiddleIncomeDispatchResponse | undefined>,
      default: undefined,
    },
    /** 完整协议结果 */
    protocolResult: {
      type: Object as PropType<MiddleIncomeContractListResponse | undefined>,
      default: undefined,
    },
    /** 来源标识 */
    from: {
      type: String as PropType<CreditMallFromEnum>,
      default: CreditMallFromEnum.FINANCE_SCREENING_AGREEMENT,
    },
    /** 场景标识 */
    scene: {
      type: String,
      default: '',
    },
    /** 资料是否不完整，true 不完整，false 完整 */
    incompleteInfo: {
      type: Boolean,
      default: false,
    },
    /** 最高可申请金额 */
    applyAmount: {
      type: String,
      default: '200000',
    },
    /** 是否开启倒计时 */
    enableCountdown: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['apply-success', 'apply-fail'],
  setup(props, { emit }) {
    /** 倒计时秒数 */
    const countdown = ref(5);
    /** 倒计时定时器 */
    let countdownTimer: number | null = null;
    /** 授信状态 */
    /** 缓存进件结果 */
    const applyNoRes = ref<MiddleIncomeCreditApplyResponse>();
    const creditStatus = ref<PageStatusEnum | undefined>(undefined);
    /** 成功跳转地址 */
    const successJumpUrl = ref<string>('');

    // const creditTaskId = computed(() => applyNoRes.value?.taskId);
    const taskId = ref<string>('');

    const pickCommodityIds = (list?: Array<{ commodityId: string }>) => (
      (list || []).map((i) => i.commodityId)
    );

    /** 商城产品 id 列表（来自分发结果 shoppingMallCommodities） */
    const shoppingMallProductIds = computed(() => (
      pickCommodityIds(props.dispatchResult?.shoppingMallCommodities)
    ));

    /** 半流程产品 id 列表（来自分发结果 overLoanCommodities） */
    const semiProcessProductIds = computed(() => (
      pickCommodityIds(props.dispatchResult?.overLoanCommodities)
    ));

    /**
     * 基于协议结果拆分协议列表：
     * - 商城协议列表：protocolResult.shoppingMallContractList
     * - 半流程协议列表：protocolResult.overLoanContractList
     *
     * 同时将两者扁平化为当前展示所需的 contractList（给 ContractViewWithTab 使用）
     */
    type ContractViewItem = ContractItemDetail & { icon?: string };

    const shoppingMallContractList = computed<ProductContractList[]>(() => (
      props.protocolResult?.shoppingMallContractList || []
    ));

    const semiProcessContractList = computed<ProductContractList[]>(() => (
      props.protocolResult?.overLoanContractList || []
    ));

    const flattenProductContractList = (list?: ProductContractList[]): ContractViewItem[] => (
      (list || []).reduce<ContractViewItem[]>((acc, product) => {
        const items = (product.contractList || []).map((contract: ContractItemDetail) => ({
          ...contract,
          icon: product.productIcon,
        }));
        return acc.concat(items);
      }, [])
    );

    const contractList = computed<ContractViewItem[]>(() => {
      const mallContracts = flattenProductContractList(shoppingMallContractList.value);
      const semiContracts = flattenProductContractList(semiProcessContractList.value);
      // 有商城、无半流程：返回商城协议
      if (mallContracts.length > 0 && semiContracts.length === 0) {
        // eslint-disable-next-line no-console
        console.error('mallContracts', mallContracts);
        return mallContracts;
      }
      // 有半流程、无商城：返回半流程协议
      if (semiContracts.length > 0 && mallContracts.length === 0) {
        // eslint-disable-next-line no-console
        console.error('semiContracts', semiContracts);
        return semiContracts;
      }
      // 两个都有（或两个都无）：拼接（保持商城在前）
      // eslint-disable-next-line no-console
      console.error('mallContracts', mallContracts);
      // eslint-disable-next-line no-console
      console.error('semiContracts', semiContracts);
      return mallContracts.concat(semiContracts);
    });

    /** 格式化金额 */
    const formatAmount = computed(() => {
      const amount = props.applyAmount;
      // if (typeof amount === 'number' || (typeof amount === 'string' && Number(amount))) {
      //   return Number(amount).toLocaleString();
      // }
      return amount;
    });

    /** 标题后缀：商城展示信用付文案，半流程展示借钱优选文案；两者都有时优先信用付 */
    const titleSuffix = computed(() => {
      if (shoppingMallProductIds.value.length > 0) {
        return '信用付额度待申请';
      }
      if (semiProcessProductIds.value.length > 0) {
        return '借钱优选额度待申请';
      }
      return '信用付额度待申请';
    });

    /** 按钮是否禁用 */
    const isButtonDisabled = computed(() => (
      (props.enableCountdown && countdown.value > 0)
      || creditStatus.value === PageStatusEnum.PROCESSING
    ));

    /** 按钮文案 */
    const buttonText = computed(() => {
      if (props.enableCountdown && countdown.value > 0) {
        return `已阅读并同意协议(${countdown.value}s)`;
      }
      if (creditStatus.value === PageStatusEnum.PROCESSING) {
        return '授信中...';
      }
      if (creditStatus.value === PageStatusEnum.SUCCEEDED) {
        return '去使用';
      }
      return '已阅读并同意协议';
    });

    /**
     * 启动倒计时
     */
    const startCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
      countdown.value = 5;
      countdownTimer = window.setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
          }
        }
      }, 1000);
    };

    /**
     * 轮询授信状态
     */
    const polling = pollingStatus({
      // creditTaskId,
      from: props.from,
      taskId: () => taskId.value,
      onStatusUpdate: (status) => {
        creditStatus.value = status;
      },
      onSuccess: async (res) => {
        // 授信成功
        if (res?.jumpUrl) {
          successJumpUrl.value = res.jumpUrl;
          emit('apply-success', res);
          openThirdPage(res.jumpUrl);
        } else {
          Toast('未能获取到跳转地址，请稍后再试');
        }
      },
      onFailed: () => {
        Toast('本次消费分期额度审核失败，保持良好信用下次再试');
        emit('apply-fail');
      },
      onTimeout: () => {
        Toast('未查询到开通结果');
      },
    });

    /**
     * 请求分期商城进件接口
     */
    const requestApply = async () => {
      try {
        creditStatus.value = PageStatusEnum.PROCESSING;
        const {
          applyMode, priorityLevel,
        } = props.dispatchResult || {};

        // 调用中收授信申请接口（商城授信场景）
        const response = await applyMiddleIncomeCredit({
          from: props.from,
          shoppingMallCommodityList: shoppingMallProductIds.value,
          overLoanCommodityList: semiProcessProductIds.value,
          scene: props.scene,
          incompleteInfo: props.incompleteInfo,
          applyMode,
          priorityLevel,
        });
        // applyNoRes.value = response;

        if (response?.taskId) {
          taskId.value = response.taskId;
          /** 申请成功，执行申请成功回调 */
          emit('apply-success', response);
          // polling.start();
        } else {
          Toast('申请失败，请稍后重试');
          creditStatus.value = PageStatusEnum.INIT;
        }
      } catch (error: any) {
        Toast('申请失败，请稍后重试');
        creditStatus.value = PageStatusEnum.INIT;
      }
    };

    /**
     * 处理申请按钮点击
     */
    const handleApply = async () => {
      if (shoppingMallProductIds.value.length > 0) {
        ubtPageButtonNew(router.currentRoute.meta?.pageId as string, shopMallAgreementPopupClick, {
          from: props.from,
          productIds: shoppingMallProductIds.value,
        });
      } else if (semiProcessProductIds.value.length > 0) {
        ubtPageButtonNew(router.currentRoute.meta?.pageId as string, dcHalfApplyAgreementClick, {
          from: props.from,
        });
      }
      // 如果在倒计时或授信中，不处理
      if (isButtonDisabled.value) {
        return;
      }

      // 如果已经授信成功，跳转
      if (creditStatus.value === PageStatusEnum.SUCCEEDED) {
        if (successJumpUrl.value) {
          openThirdPage(successJumpUrl.value);
        } else {
          Toast('未能获取到跳转地址，请稍后再试');
        }
        return;
      }

      // 发起授信申请
      await requestApply();
    };

    onMounted(() => {
      if (shoppingMallProductIds.value.length > 0) {
        ubtModuleExposeNew(router.currentRoute.meta?.pageId as string,
          shopMallAgreementPopupExpose, {
            from: props.from,
            productIds: shoppingMallProductIds.value,
          });
      }
      if (semiProcessProductIds.value.length > 0) {
        ubtModuleExposeNew(router.currentRoute.meta?.pageId as string, dcHalfApplyAgreementExpose, {
          from: props.from,
        });
      }
      // 组件挂载后，如果开启了倒计时，则启动倒计时
      if (props.enableCountdown) {
        startCountdown();
      }
    });

    onBeforeUnmount(() => {
      // 清理定时器
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
      // 停止轮询
      polling.cancel();
    });

    return {
      countdown,
      creditStatus,
      contractList,
      shoppingMallContractList,
      semiProcessContractList,
      formatAmount,
      titleSuffix,
      isButtonDisabled,
      buttonText,
      handleApply,
      shoppingMallProductIds,
      semiProcessProductIds,
      PageStatusEnum,
    };
  },
});
